/**
 * email-signup-scope.js — client-side scoping for newsletter signup confirmations.
 *
 * WHY THIS EXISTS
 * Multiple {% form 'customer' %} instances can share a page (footer signup + the
 * homepage subscription teaser). Scoping their success state server-side proved
 * impossible; two live tests settled it:
 *   - Submitting the TEASER rendered the confirmation on BOTH the teaser and the
 *     footer, so `form.id` does not round-trip discriminately — the tolerant
 *     server-side boolean fires on every instance.
 *   - Submitting the FOOTER rendered NO confirmation anywhere, so its return state
 *     doesn't even carry `form.posted_successfully?` reliably.
 * Server-side Liquid therefore cannot tell which instance was submitted, and in the
 * footer case cannot tell that anything was submitted at all. The client can: it was
 * there when the submit happened.
 *
 * MECHANISM
 *   1. On submit, stash the submitting form's stable id (+ a timestamp) in
 *      sessionStorage, before the POST navigates away.
 *   2. On the next load, if a FRESH stash exists, reveal the confirmation on that
 *      form only, hide it on the others, scroll the confirmed one into view, clear
 *      the stash.
 *
 * The stash alone is the trigger — deliberately NOT `customer_posted=true` or the
 * presence of server-rendered success markup. Both are absent in the footer case,
 * which is the bug we're fixing; requiring either would reproduce it. A stash can
 * only exist because this browser just submitted one of these forms.
 *
 * DEGRADATION
 *   - No JS: the server render stands — a confirmation may appear on both forms.
 *     Degraded but functional, and never silent-on-success for the teaser.
 *   - Stash miss (back button, cross-tab, expired): leave the server render exactly
 *     as-is. A possibly-duplicated confirmation beats a missing one.
 *   - Errors present: never reveal success. The server's error branch wins.
 *
 * DOM CONTRACT (see sections/../blocks/email-signup.liquid and the teaser block in
 * templates/index.json):
 *   [data-signup-form="<stable-id>"]  the <form>
 *   [data-signup-success]             confirmation, ALWAYS rendered, `hidden` unless
 *                                     the server already decided to show it
 *   [data-signup-fields]              field + button wrapper, hidden on success
 *   [data-signup-error]               error message, when present suppresses success
 */

(() => {
  if (window.__soSignupScopeInit) return;
  window.__soSignupScopeInit = true;

  const KEY = 'so-signup-submitted';
  const MAX_AGE_MS = 120000; // a stash older than this is stale, not a fresh return

  const read = () => {
    try {
      const raw = sessionStorage.getItem(KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.id) return null;
      if (Date.now() - (parsed.t || 0) > MAX_AGE_MS) return null;
      return parsed.id;
    } catch (e) {
      return null;
    }
  };

  const clear = () => {
    try {
      sessionStorage.removeItem(KEY);
    } catch (e) {
      /* sessionStorage unavailable (private mode / blocked) — degrade to server render */
    }
  };

  // Capture phase so we stash even if something else stops propagation.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target && event.target.closest ? event.target.closest('[data-signup-form]') : null;
      if (!form) return;
      try {
        sessionStorage.setItem(KEY, JSON.stringify({ id: form.getAttribute('data-signup-form'), t: Date.now() }));
      } catch (e) {
        /* ignore — no stash means we fall back to the server render */
      }
    },
    true
  );

  const apply = () => {
    const forms = document.querySelectorAll('[data-signup-form]');
    if (!forms.length) return;

    const submittedId = read();
    if (!submittedId) return; // stash miss — leave the server render untouched

    let confirmed = null;

    forms.forEach((form) => {
      const success = form.querySelector('[data-signup-success]');
      const fields = form.querySelector('[data-signup-fields]');
      const hasError = !!form.querySelector('[data-signup-error]');
      if (!success) return;

      const isSubmitter = form.getAttribute('data-signup-form') === submittedId;

      if (isSubmitter && !hasError) {
        success.hidden = false;
        if (fields) fields.hidden = true;
        confirmed = form;
      } else {
        // Every other instance goes back to its field state, undoing the
        // server's tolerant boolean firing everywhere.
        success.hidden = true;
        if (fields) fields.hidden = false;
      }
    });

    clear();

    if (confirmed) {
      const success = confirmed.querySelector('[data-signup-success]');
      if (success && typeof success.scrollIntoView === 'function') {
        success.scrollIntoView({ block: 'center' });
      }
      if (success && typeof success.focus === 'function') success.focus();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }
})();
